import { Link, useSearchParams } from 'react-router';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  Input,
  Pagination,
  type Column,
} from '@/components/ui';
import { useDeletedAppAccounts } from '@/features/deleted-app-accounts/api';
import type { DeletedAppAccount } from '@/features/deleted-app-accounts/types';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './patients.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

const PAGE_SIZE = 20;

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: CLINIC_TZ,
  });

const STATUS_LABEL: Record<
  DeletedAppAccount['records'][number]['patient']['status'],
  string
> = {
  active: 'Active',
  inactive: 'Inactive',
  deceased: 'Deceased',
  merged: 'Merged',
};

/** Why a link ended before the deletion (rare); the deletion itself needs no label. */
const endedEarlier = (reason: string | null) =>
  reason && reason !== 'account_deleted'
    ? `Unlinked earlier (${reason})`
    : null;

/**
 * RL-1257: the app accounts patients deleted (API RL-1182), with the clinic
 * records each one held. Reached from Patients; the clinic record and its
 * history stay, so each row links to it.
 */
export function DeletedAppAccountsPage() {
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const { data, isLoading, error, refetch } = useDeletedAppAccounts({
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const setFilter = (key: 'from' | 'to', value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const columns: Column<DeletedAppAccount>[] = [
    {
      key: 'deleted',
      header: 'Deleted on',
      render: (a) => (
        <div>
          <p className={styles.name}>{day(a.deletedAt)}</p>
          <p className={styles.sub}>Account made {day(a.createdAt)}</p>
        </div>
      ),
    },
    {
      key: 'records',
      header: 'Clinic records it held',
      render: (a) => (
        <ul className={styles.recordList}>
          {a.records.map((r) => (
            <li key={`${r.patient.id}:${r.linkedAt}`}>
              <Link to={`/patients/${r.patient.id}`}>{r.patient.fullName}</Link>{' '}
              {r.patient.status !== 'active' && (
                <Badge tone="neutral">{STATUS_LABEL[r.patient.status]}</Badge>
              )}
              <p className={styles.sub}>
                {r.branch.name} · linked {day(r.linkedAt)}
                {endedEarlier(r.revokedReason) &&
                  ` · ${endedEarlier(r.revokedReason)}`}
              </p>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'account',
      header: 'App account',
      render: (a) => <code className={styles.sub}>{a.accountId}</code>,
    },
  ];

  const meta = data?.meta;
  return (
    <PageContainer>
      <Card
        title="Deleted app accounts"
        actions={
          <span className={styles.count}>
            Clinic records are kept; only the app account is gone
            {meta ? ` · ${meta.total} in total` : ''}
          </span>
        }
        flush
      >
        <div className={styles.searchBar}>
          <Input
            type="date"
            aria-label="Deleted from"
            value={from}
            max={to || undefined}
            onChange={(e) => setFilter('from', e.target.value)}
          />
          <Input
            type="date"
            aria-label="Deleted to"
            value={to}
            min={from || undefined}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </div>
        <DataTable
          caption="Deleted app accounts"
          columns={columns}
          rows={data?.data}
          rowKey={(a) => a.accountId}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title={
                from || to
                  ? 'No app account was deleted in these dates'
                  : 'No app account has been deleted'
              }
              body="When a patient deletes their app account, it appears here with the clinic records it held."
            />
          }
          footer={
            meta && meta.total > meta.pageSize ? (
              <Pagination
                meta={meta}
                onPageChange={(next) => {
                  const p = new URLSearchParams(params);
                  p.set('page', String(next));
                  setParams(p);
                }}
              />
            ) : undefined
          }
        />
      </Card>
    </PageContainer>
  );
}
